import { storage, auth, db } from "../../../firebase";
import { doc, collection } from "firebase/firestore";
import { ref, uploadBytesResumable } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { createAttachment } from "./attachments/mutations";
import { 
  validateFile, 
  createAttachmentError,
  ATTACHMENT_ERROR_CODES
} from "../utils/attachmentValidation";

// Bandwidth tracking
class BandwidthTracker {
  static STORAGE_KEY = "affin_upload_bandwidth";
  static WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
  static LIMIT_BYTES = 500 * 1024 * 1024; // 500MB

  static getUsage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  static async checkAvailable(bytes) {
    const usage = this.getUsage();
    const now = Date.now();

    // Filter to last 24 hours
    const recentUsage = usage
      .filter((u) => now - u.timestamp < this.WINDOW_MS)
      .reduce((sum, u) => sum + u.bytes, 0);

    return recentUsage + bytes <= this.LIMIT_BYTES;
  }

  static recordUsage(bytes) {
    const usage = this.getUsage();
    usage.push({ bytes, timestamp: Date.now() });

    // Keep only last 24 hours
    const cutoff = Date.now() - this.WINDOW_MS;
    const filtered = usage.filter((u) => u.timestamp > cutoff);

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
    } catch (e) {
      console.warn("Failed to save bandwidth usage:", e);
    }
  }

  static getRemainingQuota() {
    const usage = this.getUsage();
    const now = Date.now();

    const recentUsage = usage
      .filter((u) => now - u.timestamp < this.WINDOW_MS)
      .reduce((sum, u) => sum + u.bytes, 0);

    return Math.max(0, this.LIMIT_BYTES - recentUsage);
  }
}

// Media metadata extraction utilities
class MediaMetadataExtractor {
  /**
   * Extract dimensions from an image file
   * @param {File} file - Image file
   * @returns {Promise<{width: number, height: number}>}
   */
  static async getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  /**
   * Extract duration from a video file
   * @param {File} file - Video file
   * @returns {Promise<number>} Duration in seconds
   */
  static async getVideoDuration(file) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(file);
      
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = url;
    });
  }

  /**
   * Extract video dimensions
   * @param {File} file - Video file
   * @returns {Promise<{width: number, height: number}>}
   */
  static async getVideoDimensions(file) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(file);
      
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: video.videoWidth,
          height: video.videoHeight
        });
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = url;
    });
  }
}

// Upload service
class UploadService {
  constructor() {
    this.activeUploads = new Map();
    this.uploadQueue = [];
    this.maxConcurrent = 3; // Limit concurrent uploads
  }


  // Compress image if needed
  async processImage(file) {
    const options = {
      maxSizeMB: 2, // Compress to max 2MB
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: file.type
    };

    try {
      // Only compress if larger than 2MB
      if (file.size > 2 * 1024 * 1024) {
        console.log(`Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        const compressed = await imageCompression(file, options);
        console.log(`Compressed to: ${(compressed.size / 1024 / 1024).toFixed(2)}MB`);
        return compressed;
      }
      return file;
    } catch (error) {
      console.warn("Image compression failed, using original:", error);
      return file;
    }
  }

  // Generate storage path based on context (temp or permanent)
  generatePath(recordId, attachmentId, filename, sessionId = null, userId = null) {
    const extension = filename.split('.').pop() || 'bin';
    
    // If sessionId is provided, use temp path
    if (sessionId && userId) {
      return `temp/${userId}/${sessionId}/${attachmentId}/original.${extension}`;
    }
    
    // Otherwise use permanent path (for existing records)
    return `records/${recordId}/attachments/${attachmentId}/original.${extension}`;
  }

  // Upload single file
  async uploadFile(file, recordId, onProgress, sessionId = null) {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      throw createAttachmentError(validation.errorCode);
    }

    // Check bandwidth
    if (!(await BandwidthTracker.checkAvailable(file.size))) {
      throw createAttachmentError(ATTACHMENT_ERROR_CODES.DAILY_LIMIT_REACHED);
    }

    // Get current user
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw createAttachmentError(ATTACHMENT_ERROR_CODES.NOT_AUTHENTICATED);
    }

    // Process file (compress images)
    let processedFile = file;
    if (validation.fileType === "image") {
      processedFile = await this.processImage(file);
    }

    // Generate unique attachment ID using Firestore's auto-id algorithm
    const attachmentId = doc(collection(db, '_')).id;
    
    // Generate storage path (temp or permanent based on sessionId)
    const path = this.generatePath(recordId, attachmentId, file.name, sessionId, userId);
    const storageRef = ref(storage, path);

    // Extract metadata based on file type
    let metadata = {
      width: undefined,
      height: undefined,
      duration: undefined
    };

    try {
      if (validation.fileType === "image") {
        const dimensions = await MediaMetadataExtractor.getImageDimensions(processedFile);
        metadata.width = dimensions.width;
        metadata.height = dimensions.height;
      } else if (validation.fileType === "video") {
        const [dimensions, duration] = await Promise.all([
          MediaMetadataExtractor.getVideoDimensions(file),
          MediaMetadataExtractor.getVideoDuration(file)
        ]);
        metadata.width = dimensions.width;
        metadata.height = dimensions.height;
        metadata.duration = duration;
      }
    } catch (error) {
      console.warn('Failed to extract media metadata:', error);
      // Continue without metadata - it's not critical
    }

    // Create upload task
    const uploadTask = uploadBytesResumable(storageRef, processedFile, {
      contentType: file.type,
      customMetadata: {
        uploadedBy: userId,
        originalName: file.name,
        recordId: recordId || 'pending',
        sessionId: sessionId || 'direct'
      }
    });

    // Return promise that tracks upload
    return new Promise((resolve, reject) => {
      this.activeUploads.set(attachmentId, uploadTask);

      // Store listener reference for cleanup
      const unsubscribe = uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          // Pass file info along with progress
          onProgress?.(progress, attachmentId, {
            file: file,
            size: processedFile.size
          });
        },
        (error) => {
          this.activeUploads.delete(attachmentId);
          reject(error);
        },
        async () => {
          try {
            // Record bandwidth usage
            BandwidthTracker.recordUsage(processedFile.size);

            // Create attachment document data
            const attachmentData = {
              type: validation.fileType,
              mimeType: file.type,
              storagePath: path,
              name: file.name,
              sizeBytes: processedFile.size,
              thumbnails: {} // Will be populated by backend
            };

            // Add optional metadata
            if (metadata.width !== undefined) {
              attachmentData.width = metadata.width;
            }
            if (metadata.height !== undefined) {
              attachmentData.height = metadata.height;
            }
            if (metadata.duration !== undefined) {
              attachmentData.duration = metadata.duration;
            }

            // Only create Firestore document if we don't have a sessionId (direct permanent upload)
            if (recordId && !sessionId) {
              // Create attachment document in Firestore
              const createdAttachment = await createAttachment(recordId, attachmentId, attachmentData);
              this.activeUploads.delete(attachmentId);
              resolve(createdAttachment);
            } else {
              // For temp uploads (including edits with sessionId), return attachment data without Firestore document
              this.activeUploads.delete(attachmentId);
              resolve({
                id: attachmentId,
                fileId: attachmentId, // Include fileId for migration
                ...attachmentData,
                uploadedAt: new Date(),
                isTemp: true
              });
            }
          } catch (error) {
            this.activeUploads.delete(attachmentId);
            reject(error);
          }
        }
      );

      // Store unsubscribe function with the upload task
      uploadTask._unsubscribe = unsubscribe;
    });
  }

  // Upload multiple files
  async uploadMultiple(files, recordId, onProgress, sessionId = null) {
    const results = [];
    const errors = [];

    // Process files with concurrency limit
    const chunks = [];
    for (let i = 0; i < files.length; i += this.maxConcurrent) {
      chunks.push(files.slice(i, i + this.maxConcurrent));
    }

    for (const chunk of chunks) {
      const promises = chunk.map((file) =>
        this.uploadFile(file, recordId, onProgress, sessionId)
          .then((attachment) => {
            const pair = { attachment, originalFile: file };
            results.push(pair);
            return pair;
          })
          .catch((error) => {
            errors.push({ file: file.name, error: error.message });
            return null;
          })
      );

      await Promise.all(promises);
    }

    return { results, errors };
  }

}

// Export singleton instance
export const uploadService = new UploadService();

// Export main functions for component use
export async function uploadRecordAttachments(files, recordId, onProgress, sessionId = null) {
  // Convert FileList to array
  const fileArray = Array.from(files);
  
  // Track overall progress
  const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
  const progressMap = new Map(); // Map uploadId to progress percentage
  
  const progressHandler = (progress, uploadId, fileInfo) => {
    // Update progress for this specific upload
    progressMap.set(uploadId, {
      size: fileInfo.size,
      progress: progress
    });
    
    // Calculate total bytes transferred across all uploads
    let bytesTransferred = 0;
    progressMap.forEach(({ size, progress }) => {
      bytesTransferred += (size * progress) / 100;
    });
    
    onProgress?.(bytesTransferred, totalSize);
  };
  
  const { results, errors } = await uploadService.uploadMultiple(
    fileArray,
    recordId,
    progressHandler,
    sessionId
  );
  
  if (errors.length > 0) {
    console.warn('Some files failed to upload:', errors);
  }
  
  // Return results which already contain { attachment, originalFile } pairs
  return results.filter(Boolean); // Filter out null results from failed uploads
}
